import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useQuery, ApolloProvider } from '@apollo/client';
import MarketplaceFeed from '../components/Home/MarketplaceFeed';
import SuccessionDrawerWithListener from './Succession/SuccessionDrawerWithListener';
import client from '../lib/apolloClient';

// Slim query for Marketplace feed
const MARKETPLACE_USER_SEARCH = gql`
  query MarketplaceUserSearch(
    $page: Int,
    $perPage: Int,
    $locationWithin: String
  ) {
    userSearch(
      page: $page,
      perPage: $perPage,
      locationWithin: $locationWithin
    ) {
      total_count
      items {
        id
        user_name
        fb_user_name
        allowed_name
        city
        province
        gross_production
        assets_under_management
        number_of_clients
        years_practicing
        profile_picture
        primary_markets
        specialized_businesses
      }
    }
  }
`;

const MarketplaceFeedInner = () => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const firstLoadTracked = useRef(false);

  const variables = useMemo(() => ({
    page,
    perPage: 20,
    locationWithin: '200', // 200 km nearest
  }), [page]);

  const { data, loading, error, fetchMore } = useQuery(MARKETPLACE_USER_SEARCH, {
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Merge pages
  useEffect(() => {
    const incoming = data?.userSearch?.items || [];
    if (incoming.length) {
      setItems(prev => (page === 1 ? incoming : [...prev, ...incoming]));

      // Track first load once
      if (!firstLoadTracked.current) {
        try { if (window.ahoy) window.ahoy.track('home_feed_load', { total: data?.userSearch?.total_count || 0 }); } catch (_) {}
        firstLoadTracked.current = true;
      }
    } else if (page === 1 && !loading && !error) {
      setItems([]);
    }
  }, [data, page, loading, error]);

  const hasMore = useMemo(() => {
    const total = data?.userSearch?.total_count || 0;
    return items.length < total;
  }, [data?.userSearch?.total_count, items.length]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    fetchMore({
      variables: { ...variables, page: nextPage },
      updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
    }).then(() => setPage(nextPage));
  }, [hasMore, loading, fetchMore, variables, page]);

  return (
    <>
      <MarketplaceFeed
        users={items}
        loading={loading && page === 1}
        error={error}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />
      <SuccessionDrawerWithListener />
    </>
  );
};

const MarketplaceFeedContainer = () => (
  <ApolloProvider client={client}>
    <MarketplaceFeedInner />
  </ApolloProvider>
);

export default MarketplaceFeedContainer;


